import React, { useState, useEffect, useRef } from 'react';
import { 
  Video, Mic, MicOff, VideoOff, Phone, ScreenShare, Disc, 
  Users, MessageSquare, Shield, Settings, Copy, Check, QrCode, 
  Maximize2, Minimize2, ExternalLink, Sparkles, Smile, Hand, 
  AlertTriangle, Lock, Unlock, Download, PenTool, Eraser, 
  RotateCcw, X, ChevronUp, ChevronDown, CheckCheck, Grid, 
  UserCheck, UserX, Volume2, Globe, FileText, Share2, PictureInPicture
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ProfessionalLegalWhiteboard from './ProfessionalLegalWhiteboard';

export interface ConferenceProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId?: string;
  meetingTopic?: string;
  isHost?: boolean;
}

interface Participant {
  id: string;
  name: string;
  role: 'Host' | 'Co-Host' | 'Advocate' | 'Client' | 'Judge';
  isMuted: boolean;
  isVideoOff: boolean;
  isHandRaised: boolean;
  avatarUrl: string;
  isSpeaking?: boolean;
}

interface ChatMsg {
  id: string;
  sender: string;
  role: string;
  text: string;
  time: string;
  isCitation?: boolean;
}

export const ElumVideoConferenceModal: React.FC<ConferenceProps> = ({
  isOpen,
  onClose,
  meetingId: initialMeetingId,
  meetingTopic = 'ELum Judicial & Legal Strategy Conference',
  isHost = true
}) => {
  const { user } = useAuth();
  const { colors } = useTheme();

  // MEETING CREDENTIALS
  const [meetingId] = useState<string>(initialMeetingId || `${Math.floor(100 + Math.random() * 900)} ${Math.floor(100 + Math.random() * 900)} ${Math.floor(1000 + Math.random() * 9000)}`);
  const [passcode] = useState<string>('elum2026');
  const [meetingUrl, setMeetingUrl] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);

  // WINDOW-IN-WINDOW (PiP) & MODAL VIEW STATES
  // 'fullscreen' | 'modal' | 'pip' (Window-in-Window floating mini window)
  const [windowMode, setWindowMode] = useState<'fullscreen' | 'modal' | 'pip'>('fullscreen');
  const [layoutMode, setLayoutMode] = useState<'gallery' | 'speaker'>('gallery');
  const [pipPosition, setPipPosition] = useState<{ x: number; y: number }>({ x: 24, y: 24 });

  // MEDIA CONTROLS & STREAMS
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [virtualBg, setVirtualBg] = useState<'none' | 'blur' | 'court' | 'bench' | 'library'>('none');
  
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const pipVideoRef = useRef<HTMLVideoElement | null>(null);
  const screenVideoRef = useRef<HTMLVideoElement | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  // SUPERUSER / DATABASE ADMINISTRATOR AUDIO & VIDEO RECORDING
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [recordedBlobs, setRecordedBlobs] = useState<Blob[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [showRecordingRestrictedModal, setShowRecordingRestrictedModal] = useState(false);
  const [allowAttendeeRecording, setAllowAttendeeRecording] = useState(false);

  // Checks if active user has Superuser / DB Admin privileges
  const isSuperUserOrAdmin = Boolean(
    user?.role === 'superuser' || 
    user?.role === 'admin' || 
    user?.email === 'elumlawassociates@gmail.com' || 
    (user as any)?.canRecord === true
  );

  const canRecordMeeting = isSuperUserOrAdmin || allowAttendeeRecording;

  // ACTIVE PANELS / DRAWERS
  const [activeDrawer, setActiveDrawer] = useState<'none' | 'participants' | 'chat' | 'security' | 'whiteboard' | 'invite'>('none');
  
  // IN-MEETING CHAT
  const [chatMessages, setChatMessages] = useState<ChatMsg[]>([
    { id: '1', sender: 'ELum Judicial Bot', role: 'System', text: '🔒 Session initialized with 256-Bit TLS End-to-End Judicial Encryption.', time: 'Just now' },
    { id: '2', sender: 'Advocate M. Kalat', role: 'Host', text: 'Welcome colleagues. The hearing records & draft citations are ready for live review.', time: 'Just now' }
  ]);
  const [chatInput, setChatInput] = useState('');

  // PARTICIPANTS ROSTER
  const [participants, setParticipants] = useState<Participant[]>([
    {
      id: 'me',
      name: user?.name ? `${user.name} (Me)` : 'Advocate Kalat (Host / Me)',
      role: 'Host',
      isMuted: false,
      isVideoOff: false,
      isHandRaised: false,
      avatarUrl: (user as any)?.avatarUrl || (user?.email === 'elumlawassociates@gmail.com' ? '/owner.jpeg' : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Advocate')}&background=0284C7&color=fff`),
      isSpeaking: true
    },
    {
      id: 'p1',
      name: 'Justice Chambers (Consultant)',
      role: 'Judge',
      isMuted: false,
      isVideoOff: false,
      isHandRaised: false,
      avatarUrl: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80&w=150',
      isSpeaking: false
    },
    {
      id: 'p2',
      name: 'Barrister Sarah Khan',
      role: 'Co-Host',
      isMuted: true,
      isVideoOff: false,
      isHandRaised: false,
      avatarUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=150',
      isSpeaking: false
    },
    {
      id: 'p3',
      name: 'Corporate Counsel Lead',
      role: 'Advocate',
      isMuted: true,
      isVideoOff: true,
      isHandRaised: false,
      avatarUrl: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80&w=150',
      isSpeaking: false
    }
  ]);

  // SECURITY SETTINGS
  const [isMeetingLocked, setIsMeetingLocked] = useState(false);
  const [isWaitingRoomEnabled, setIsWaitingRoomEnabled] = useState(true);
  const [allowParticipantScreenShare, setAllowParticipantScreenShare] = useState(true);
  const [allowParticipantChat, setAllowParticipantChat] = useState(true);
  const [allowParticipantUnmute, setAllowParticipantUnmute] = useState(true);

  // REACTIONS & EMOJIS
  const [floatingReactions, setFloatingReactions] = useState<{ id: number; emoji: string; x: number }[]>([]);
  const [isHandRaised, setIsHandRaised] = useState(false);

  // 1. Initialize Meeting Link
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const cleanId = meetingId.replace(/\s+/g, '');
      const url = `${window.location.origin}/conference?meetingId=${cleanId}&pwd=${passcode}`;
      setMeetingUrl(url);
    }
  }, [meetingId, passcode]);

  // 2. Initialize Camera & Audio MediaStream
  useEffect(() => {
    if (!isOpen) return;

    let activeStream: MediaStream | null = null;

    const startLocalStream = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: true
          });
          activeStream = stream;
          localStreamRef.current = stream;

          if (localVideoRef.current) {
            localVideoRef.current.srcObject = stream;
          }
          if (pipVideoRef.current) {
            pipVideoRef.current.srcObject = stream;
          }
        }
      } catch (err) {
        console.warn('WebRTC Camera/Mic access note (using simulated preview):', err);
      }
    };

    startLocalStream();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach(track => track.stop());
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen]);

  // Sync Video Ref when PiP mode toggles
  useEffect(() => {
    if (localStreamRef.current) {
      if (windowMode === 'pip' && pipVideoRef.current) {
        pipVideoRef.current.srcObject = localStreamRef.current;
      } else if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    }
  }, [windowMode]);

  // 3. Recording Timer
  useEffect(() => {
    let interval: any;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingSeconds(s => s + 1);
      }, 1000);
    } else {
      setRecordingSeconds(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Format Timer HH:MM:SS
  const formatTime = (secs: number) => {
    const hrs = Math.floor(secs / 3600);
    const mins = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Toggle Camera
  const toggleVideo = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getVideoTracks().forEach(track => {
        track.enabled = isVideoOff;
      });
    }
    setIsVideoOff(!isVideoOff);
    setParticipants(prev => prev.map(p => p.id === 'me' ? { ...p, isVideoOff: !isVideoOff } : p));
  };

  // Toggle Mic
  const toggleAudio = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
    }
    setIsMuted(!isMuted);
    setParticipants(prev => prev.map(p => p.id === 'me' ? { ...p, isMuted: !isMuted } : p));
  };

  // Screen Sharing via getDisplayMedia
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
        screenStreamRef.current = null;
      }
      setIsScreenSharing(false);
    } else {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
          const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
          screenStreamRef.current = displayStream;
          if (screenVideoRef.current) {
            screenVideoRef.current.srcObject = displayStream;
          }
          displayStream.getVideoTracks()[0].onended = () => {
            setIsScreenSharing(false);
          };
          setIsScreenSharing(true);
        } else {
          alert('Screen sharing is not supported by this browser.');
        }
      } catch (e) {
        console.warn('Screen share cancelled or failed:', e);
      }
    }
  };

  // Superuser / Admin Audio-Video Recording Handler
  const handleToggleRecording = () => {
    if (!canRecordMeeting) {
      setShowRecordingRestrictedModal(true);
      return;
    }

    if (isRecording) {
      // STOP RECORDING
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
    } else {
      // START RECORDING
      try {
        const streamToRecord = screenStreamRef.current || localStreamRef.current;
        if (streamToRecord && typeof MediaRecorder !== 'undefined') {
          const recorder = new MediaRecorder(streamToRecord, {
            mimeType: MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus') 
              ? 'video/webm;codecs=vp9,opus' 
              : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : ''
          });

          const chunks: Blob[] = [];
          recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunks.push(e.data);
          };

          recorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'video/webm' });
            setRecordedBlobs([blob]);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ELum_Judicial_Conference_${new Date().toISOString().slice(0, 10)}.webm`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
          };

          recorder.start(1000);
          mediaRecorderRef.current = recorder;
          setIsRecording(true);
        } else {
          setIsRecording(true);
        }
      } catch (err) {
        console.warn('MediaRecorder init fallback:', err);
        setIsRecording(true);
      }
    }
  };

  // Trigger Native Browser Picture-in-Picture
  const triggerNativePiP = async () => {
    try {
      if (localVideoRef.current && document.pictureInPictureEnabled) {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await localVideoRef.current.requestPictureInPicture();
        }
      } else {
        setWindowMode('pip');
      }
    } catch (e) {
      setWindowMode('pip');
    }
  };

  // Floating Reaction Emojis
  const sendReaction = (emoji: string) => {
    const id = Date.now() + Math.random();
    const x = 30 + Math.random() * 40;
    setFloatingReactions(prev => [...prev, { id, emoji, x }]);
    setTimeout(() => {
      setFloatingReactions(prev => prev.filter(r => r.id !== id));
    }, 2500);
  };

  // Toggle Hand Raise
  const toggleHandRaise = () => {
    const newState = !isHandRaised;
    setIsHandRaised(newState);
    setParticipants(prev => prev.map(p => p.id === 'me' ? { ...p, isHandRaised: newState } : p));
    if (newState) {
      sendReaction('✋');
    }
  };

  // Send Chat Message
  const handleSendMessage = () => {
    if (!chatInput.trim()) return;
    const isCitation = chatInput.includes('SCMR') || chatInput.includes('PLD') || chatInput.includes('CLC') || chatInput.includes('PCrLJ') || chatInput.includes('MLD');
    const newMsg: ChatMsg = {
      id: Date.now().toString(),
      sender: user?.name || 'Advocate Kalat',
      role: 'Host',
      text: chatInput.trim(),
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isCitation
    };
    setChatMessages(prev => [...prev, newMsg]);
    setChatInput('');
  };

  // Quick Insert Citation
  const insertCitation = (cit: string) => {
    setChatInput(prev => `${prev} ${cit}`.trim());
  };

  // Copy Meeting Link to Clipboard
  const handleCopyLink = () => {
    if (navigator.clipboard && meetingUrl) {
      navigator.clipboard.writeText(meetingUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2500);
    }
  };

  if (!isOpen) return null;

  // ----------------------------------------------------
  // 1. WINDOW-IN-WINDOW (PiP) FLOATING MINI-WINDOW MODE
  // ----------------------------------------------------
  if (windowMode === 'pip') {
    return (
      <div 
        style={{
          position: 'fixed',
          bottom: `${pipPosition.y}px`,
          right: `${pipPosition.x}px`,
          width: '380px',
          height: '255px',
          backgroundColor: '#090d16',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.8), 0 0 0 2px #0284c7',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          fontFamily: 'system-ui, -apple-system, sans-serif'
        }}
      >
        {/* PiP Mini Header */}
        <div style={{ backgroundColor: '#0f172a', padding: '0.4rem 0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.75rem', fontWeight: '800', color: '#ecc94b' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#22c55e', animation: 'pulse 1.5s infinite' }}></span>
            ELum Meet • {isRecording && <span style={{ color: '#ef4444', fontWeight: '900' }}>🔴 REC {formatTime(recordingSeconds)}</span>}
          </div>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button 
              onClick={() => setWindowMode('fullscreen')}
              title="Expand to Fullscreen Video Conference"
              style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Maximize2 size={13} />
            </button>
            <button 
              onClick={onClose}
              title="Leave / Close Conference"
              style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '0.2rem 0.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <X size={13} />
            </button>
          </div>
        </div>

        {/* PiP Video Viewport */}
        <div style={{ flex: 1, position: 'relative', backgroundColor: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!isVideoOff ? (
            <video 
              ref={pipVideoRef}
              autoPlay 
              playsInline 
              muted 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
              <img 
                src={(user as any)?.avatarUrl || '/owner.jpeg'} 
                alt="Avatar" 
                style={{ width: '55px', height: '55px', borderRadius: '50%', border: '2px solid #0284c7' }} 
              />
              <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8' }}>{user?.name || 'Advocate Kalat'}</span>
            </div>
          )}

          {/* Active Speaker Label */}
          <div style={{ position: 'absolute', bottom: '8px', left: '8px', backgroundColor: 'rgba(0,0,0,0.7)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', color: 'white', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {isMuted ? <MicOff size={11} color="#ef4444" /> : <Mic size={11} color="#22c55e" />}
            <span>{user?.name || 'Advocate Host'}</span>
          </div>

          <div style={{ position: 'absolute', bottom: '8px', right: '8px', backgroundColor: 'rgba(0,0,0,0.7)', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.7rem', color: '#ecc94b', fontWeight: '700' }}>
            👥 {participants.length}
          </div>
        </div>

        {/* PiP Mini Controls Bar */}
        <div style={{ backgroundColor: '#0f172a', padding: '0.5rem 0.75rem', display: 'flex', justifyContent: 'space-around', alignItems: 'center', borderTop: '1px solid #1e293b' }}>
          <button 
            onClick={toggleAudio}
            style={{ backgroundColor: isMuted ? '#ef4444' : 'rgba(255,255,255,0.15)', color: 'white', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? <MicOff size={15} /> : <Mic size={15} />}
          </button>
          <button 
            onClick={toggleVideo}
            style={{ backgroundColor: isVideoOff ? '#ef4444' : 'rgba(255,255,255,0.15)', color: 'white', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title={isVideoOff ? "Start Video" : "Stop Video"}
          >
            {isVideoOff ? <VideoOff size={15} /> : <Video size={15} />}
          </button>
          <button 
            onClick={() => setWindowMode('fullscreen')}
            style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
          >
            <Maximize2 size={13} /> Full Window
          </button>
          <button 
            onClick={onClose}
            style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', width: '32px', height: '32px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            title="End Call"
          >
            <Phone size={15} style={{ transform: 'rotate(135deg)' }} />
          </button>
        </div>
      </div>
    );
  }

  // ----------------------------------------------------
  // 2. FULLSCREEN & MODAL ZOOM-LIKE VIDEO CONFERENCE VIEW
  // ----------------------------------------------------
  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#090d16',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#f8fafc',
        overflow: 'hidden'
      }}
    >
      {/* TOP ZOOM BAR */}
      <div style={{ height: '48px', backgroundColor: '#0f172a', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1.25rem', flexShrink: 0 }}>
        
        {/* Left: Encryption Badge & Meeting Topic */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button 
            onClick={() => setActiveDrawer(activeDrawer === 'security' ? 'none' : 'security')}
            title="Judicial Security & Meeting Encryption Details"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', backgroundColor: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: '1px solid rgba(34, 197, 94, 0.3)', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer' }}
          >
            <Shield size={14} /> 256-Bit TLS Secured
          </button>

          <span style={{ fontWeight: '800', fontSize: '0.9rem', color: '#ecc94b', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            🏛️ {meetingTopic}
          </span>
          <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '600' }}>
            (ID: {meetingId})
          </span>
        </div>

        {/* Center: Live Recording Indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isRecording ? (
            <div style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', color: '#ef4444', padding: '0.2rem 0.75rem', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '0.4rem', animation: 'pulse 2s infinite' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444' }}></span>
              REC {formatTime(recordingSeconds)}
            </div>
          ) : (
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              {isSuperUserOrAdmin ? '👑 Superuser / Database Admin Session' : 'Judicial Session Active'}
            </span>
          )}
        </div>

        {/* Right: Layout Switcher & Window-in-Window (PiP) Controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button 
            onClick={() => setLayoutMode(layoutMode === 'gallery' ? 'speaker' : 'gallery')}
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#94a3b8', border: '1px solid #334155', padding: '0.25rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
          >
            <Grid size={13} /> {layoutMode === 'gallery' ? 'Speaker View' : 'Gallery View'}
          </button>

          {/* WINDOW-IN-WINDOW (PiP) TRIGGER BUTTON */}
          <button 
            onClick={() => setWindowMode('pip')}
            title="Minimize to Floating Window-in-Window (PiP) Mode so you can browse Case Laws & Statutes during call"
            style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.25rem 0.65rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', boxShadow: '0 2px 6px rgba(2, 132, 199, 0.4)' }}
          >
            <PictureInPicture size={14} /> Window-in-Window (PiP)
          </button>

          <button 
            onClick={triggerNativePiP}
            title="Native OS Picture-in-Picture"
            style={{ backgroundColor: 'rgba(255,255,255,0.08)', color: '#94a3b8', border: '1px solid #334155', padding: '0.25rem 0.5rem', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <ExternalLink size={13} />
          </button>
        </div>
      </div>

      {/* MAIN VIDEO STAGE & COLLABORATION WORKSPACE */}
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        
        {/* VIDEO / WHITEBOARD STAGE */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', padding: '1rem', backgroundColor: '#020617', overflowY: 'auto' }}>
          
          {/* FLOATING REACTIONS RENDER */}
          {floatingReactions.map(r => (
            <div 
              key={r.id} 
              style={{
                position: 'absolute',
                bottom: '80px',
                left: `${r.x}%`,
                fontSize: '2.5rem',
                zIndex: 9999,
                pointerEvents: 'none',
                animation: 'floatUp 2.5s ease-out forwards'
              }}
            >
              {r.emoji}
            </div>
          ))}

          {/* SCREEN SHARE BANNER IF ACTIVE */}
          {isScreenSharing && (
            <div style={{ backgroundColor: '#0284c7', color: 'white', padding: '0.4rem 1rem', borderRadius: '8px', marginBottom: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem', fontWeight: '800' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ScreenShare size={18} /> You are sharing your screen (Judicial Case Files & Precedents)
              </div>
              <button 
                onClick={toggleScreenShare}
                style={{ backgroundColor: '#dc2626', color: 'white', border: 'none', padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '800', cursor: 'pointer' }}
              >
                Stop Share
              </button>
            </div>
          )}

          {/* SCREEN SHARING VIEWPORT */}
          {isScreenSharing && (
            <div style={{ flex: 1, backgroundColor: '#0f172a', borderRadius: '12px', overflow: 'hidden', border: '2px solid #0284c7', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <video ref={screenVideoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
          )}

          {/* PROFESSIONAL LEGAL STRATEGY WHITEBOARD (PiP & FULLSCREEN READY) */}
          <ProfessionalLegalWhiteboard 
            isOpen={activeDrawer === 'whiteboard'}
            onClose={() => setActiveDrawer('none')}
            title={`🏛️ Strategic Legal Whiteboard — ${meetingTopic}`}
            initialMode="modal"
          />

          {/* PARTICIPANTS VIDEO TILES (GRID LAYOUT) */}
          {(!isScreenSharing || layoutMode === 'gallery') && (
            <div 
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: participants.length === 1 ? '1fr' : participants.length === 2 ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: '1rem',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px'
              }}
            >
              {/* MY LOCAL VIDEO TILE */}
              <div 
                style={{
                  backgroundColor: '#0f172a',
                  borderRadius: '16px',
                  border: isHandRaised ? '3px solid #eab308' : '2px solid #1e293b',
                  overflow: 'hidden',
                  position: 'relative',
                  aspectRatio: '16/9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                }}
              >
                {!isVideoOff ? (
                  <video 
                    ref={localVideoRef}
                    autoPlay 
                    playsInline 
                    muted 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: virtualBg === 'blur' ? 'blur(4px)' : 'none',
                      transform: 'scaleX(-1)'
                    }} 
                  />
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{ width: '85px', height: '85px', borderRadius: '50%', border: '3px solid #0284c7', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0284c7' }}>
                      <img 
                        src={(user as any)?.avatarUrl || (user?.email === 'elumlawassociates@gmail.com' ? '/owner.jpeg' : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || 'Advocate')}&background=0284C7&color=fff`)} 
                        alt="Avatar" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                    </div>
                    <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#f8fafc' }}>{user?.name || 'Advocate Kalat (Host)'}</span>
                  </div>
                )}

                {/* Hand Raise Badge */}
                {isHandRaised && (
                  <div style={{ position: 'absolute', top: '12px', left: '12px', backgroundColor: '#eab308', color: '#0f172a', padding: '0.2rem 0.55rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '0.3rem', zIndex: 10 }}>
                    ✋ Hand Raised
                  </div>
                )}

                {/* Name & Mic Indicator */}
                <div style={{ position: 'absolute', bottom: '12px', left: '12px', backgroundColor: 'rgba(0,0,0,0.75)', padding: '0.3rem 0.75rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.4rem', backdropFilter: 'blur(4px)' }}>
                  {isMuted ? <MicOff size={14} color="#ef4444" /> : <Mic size={14} color="#22c55e" />}
                  <span>{user?.name ? `${user.name} (Me)` : 'Advocate Kalat (Host)'}</span>
                </div>

                {/* Pin / Expand Quick Control */}
                <div style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', gap: '0.35rem' }}>
                  <button onClick={triggerNativePiP} title="Picture-in-Picture" style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '6px', padding: '0.3rem', cursor: 'pointer' }}>
                    <PictureInPicture size={14} />
                  </button>
                </div>
              </div>

              {/* REMOTE PARTICIPANTS TILES */}
              {participants.filter(p => p.id !== 'me').map((p) => (
                <div 
                  key={p.id}
                  style={{
                    backgroundColor: '#0f172a',
                    borderRadius: '16px',
                    border: p.isHandRaised ? '3px solid #eab308' : p.isSpeaking ? '2px solid #22c55e' : '2px solid #1e293b',
                    overflow: 'hidden',
                    position: 'relative',
                    aspectRatio: '16/9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                  }}
                >
                  {!p.isVideoOff ? (
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <img 
                        src={p.avatarUrl} 
                        alt={p.name} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                      />
                      <div style={{ position: 'absolute', top: '10px', right: '10px', backgroundColor: 'rgba(0,0,0,0.6)', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', color: '#22c55e', fontWeight: '800' }}>
                        HD 1080p
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                      <img 
                        src={p.avatarUrl} 
                        alt={p.name} 
                        style={{ width: '80px', height: '80px', borderRadius: '50%', border: '3px solid #334155' }} 
                      />
                      <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#94a3b8' }}>{p.name}</span>
                    </div>
                  )}

                  {/* Remote Participant Name Tag */}
                  <div style={{ position: 'absolute', bottom: '12px', left: '12px', backgroundColor: 'rgba(0,0,0,0.75)', padding: '0.3rem 0.75rem', borderRadius: '8px', fontSize: '0.8rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.4rem', backdropFilter: 'blur(4px)' }}>
                    {p.isMuted ? <MicOff size={14} color="#ef4444" /> : <Mic size={14} color="#22c55e" />}
                    <span>{p.name}</span>
                    <span style={{ backgroundColor: p.role === 'Judge' ? '#dc2626' : p.role === 'Host' ? '#0284c7' : '#475569', color: 'white', fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: '4px', fontWeight: '800' }}>
                      {p.role}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* SIDE PANELS (CHAT, PARTICIPANTS, SECURITY, INVITE) */}
        {activeDrawer !== 'none' && activeDrawer !== 'whiteboard' && (
          <div 
            style={{
              width: '360px',
              backgroundColor: '#0f172a',
              borderLeft: '1px solid #1e293b',
              display: 'flex',
              flexDirection: 'column',
              zIndex: 50,
              boxShadow: '-4px 0 20px rgba(0,0,0,0.5)'
            }}
          >
            {/* Drawer Header */}
            <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #1e293b', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: '800', fontSize: '1rem', color: '#ecc94b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {activeDrawer === 'participants' && <><Users size={18} /> Participants ({participants.length})</>}
                {activeDrawer === 'chat' && <><MessageSquare size={18} /> In-Meeting Judicial Chat</>}
                {activeDrawer === 'security' && <><Shield size={18} /> Meeting Security & Permissions</>}
                {activeDrawer === 'invite' && <><Share2 size={18} /> Invite to Judicial Conference</>}
              </div>
              <button onClick={() => setActiveDrawer('none')} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            {/* DRAWER CONTENT: PARTICIPANTS */}
            {activeDrawer === 'participants' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '1rem', overflowY: 'auto' }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                  <button 
                    onClick={() => setActiveDrawer('invite')}
                    style={{ flex: 1, backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.45rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
                  >
                    <Share2 size={14} /> Invite
                  </button>
                  <button 
                    onClick={() => setParticipants(prev => prev.map(p => p.id !== 'me' ? { ...p, isMuted: true } : p))}
                    style={{ flex: 1, backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', border: 'none', padding: '0.45rem', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem' }}
                  >
                    <MicOff size={14} /> Mute All
                  </button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {participants.map(p => (
                    <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <img src={p.avatarUrl} alt={p.name} style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover' }} />
                        <div>
                          <div style={{ fontSize: '0.82rem', fontWeight: '700', color: 'white' }}>{p.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{p.role}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.4rem', color: '#94a3b8' }}>
                        {p.isMuted ? <MicOff size={16} color="#ef4444" /> : <Mic size={16} color="#22c55e" />}
                        {p.isVideoOff ? <VideoOff size={16} color="#ef4444" /> : <Video size={16} color="#22c55e" />}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DRAWER CONTENT: CHAT */}
            {activeDrawer === 'chat' && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Precedent Quick Insert Pills */}
                <div style={{ padding: '0.5rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.03)', borderBottom: '1px solid #1e293b', display: 'flex', gap: '0.35rem', overflowX: 'auto' }}>
                  <button onClick={() => insertCitation('2026 SCMR 104')} style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.2rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ 2026 SCMR 104</button>
                  <button onClick={() => insertCitation('PLD 2025 SC 320')} style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.2rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ PLD 2025 SC 320</button>
                  <button onClick={() => insertCitation('Art. 199 Const.')} style={{ backgroundColor: '#7c3aed', color: 'white', border: 'none', padding: '0.2rem 0.45rem', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer', whiteSpace: 'nowrap' }}>+ Art. 199</button>
                </div>

                <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {chatMessages.map(msg => (
                    <div key={msg.id} style={{ backgroundColor: msg.isCitation ? 'rgba(2, 132, 199, 0.15)' : 'rgba(255,255,255,0.05)', border: msg.isCitation ? '1px solid #0284c7' : '1px solid rgba(255,255,255,0.05)', padding: '0.6rem 0.85rem', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                        <span style={{ fontWeight: '800', fontSize: '0.78rem', color: msg.role === 'Host' ? '#ecc94b' : '#38bdf8' }}>{msg.sender}</span>
                        <span style={{ fontSize: '0.68rem', color: '#64748b' }}>{msg.time}</span>
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#f1f5f9', lineHeight: '1.4' }}>{msg.text}</div>
                    </div>
                  ))}
                </div>

                <div style={{ padding: '0.75rem', borderTop: '1px solid #1e293b', display: 'flex', gap: '0.5rem' }}>
                  <input 
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type message or citation..."
                    style={{ flex: 1, backgroundColor: '#020617', border: '1px solid #334155', borderRadius: '6px', padding: '0.5rem 0.75rem', color: 'white', fontSize: '0.82rem', outline: 'none' }}
                  />
                  <button onClick={handleSendMessage} style={{ backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.5rem 0.85rem', borderRadius: '6px', fontWeight: '800', fontSize: '0.8rem', cursor: 'pointer' }}>
                    Send
                  </button>
                </div>
              </div>
            )}

            {/* DRAWER CONTENT: SECURITY & SUPERUSER ADMIN CONTROLS */}
            {activeDrawer === 'security' && (
              <div style={{ padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div style={{ backgroundColor: 'rgba(234, 179, 8, 0.1)', border: '1px solid #eab308', padding: '0.75rem', borderRadius: '8px', fontSize: '0.78rem', color: '#fef08a', lineHeight: '1.4' }}>
                  <strong>🔒 Judicial RBAC Governance:</strong> Audio/Video recording permissions and meeting locks are enforced under Judicial Privacy Standards.
                </div>

                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#f8fafc', marginBottom: '0.6rem' }}>Meeting Locks & Admission</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
                      <span>Lock Meeting (No new joins)</span>
                      <input type="checkbox" checked={isMeetingLocked} onChange={() => setIsMeetingLocked(!isMeetingLocked)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                    </label>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
                      <span>Enable Waiting Room</span>
                      <input type="checkbox" checked={isWaitingRoomEnabled} onChange={() => setIsWaitingRoomEnabled(!isWaitingRoomEnabled)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                    </label>
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #1e293b', paddingTop: '1rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#f8fafc', marginBottom: '0.6rem' }}>Participant Permissions</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
                      <span>Share Screen</span>
                      <input type="checkbox" checked={allowParticipantScreenShare} onChange={() => setAllowParticipantScreenShare(!allowParticipantScreenShare)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                    </label>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
                      <span>Chat Messages</span>
                      <input type="checkbox" checked={allowParticipantChat} onChange={() => setAllowParticipantChat(!allowParticipantChat)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                    </label>
                    <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#cbd5e1', cursor: 'pointer' }}>
                      <span>Unmute Themselves</span>
                      <input type="checkbox" checked={allowParticipantUnmute} onChange={() => setAllowParticipantUnmute(!allowParticipantUnmute)} style={{ cursor: 'pointer', width: '16px', height: '16px' }} />
                    </label>
                  </div>
                </div>

                {/* SUPERUSER ADMIN RECORDING TOGGLE */}
                <div style={{ borderTop: '1px solid #1e293b', paddingTop: '1rem', backgroundColor: 'rgba(2, 132, 199, 0.08)', padding: '0.85rem', borderRadius: '8px', border: '1px solid #0284c7' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '800', color: '#38bdf8', marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    👑 Superuser / Database Admin Policy
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.6rem' }}>
                    Grant or disallow standard attendees to record session audio/video.
                  </div>
                  <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: 'white', fontWeight: '700', cursor: 'pointer' }}>
                    <span>Allow Attendee Recording</span>
                    <input 
                      type="checkbox" 
                      checked={allowAttendeeRecording} 
                      onChange={() => setAllowAttendeeRecording(!allowAttendeeRecording)} 
                      disabled={!isSuperUserOrAdmin}
                      style={{ cursor: isSuperUserOrAdmin ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }} 
                    />
                  </label>
                </div>
              </div>
            )}

            {/* DRAWER CONTENT: INVITE TO CONFERENCE (QR & LINK) */}
            {activeDrawer === 'invite' && (
              <div style={{ padding: '1.25rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem', alignItems: 'center', textAlign: 'center' }}>
                <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
                  <QRCodeSVG value={meetingUrl || 'https://www.elumlaw.com/conference'} size={150} />
                </div>
                <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  Scan on mobile or tablet to join instantly without passcode entry.
                </div>

                <div style={{ width: '100%', textAlign: 'left', backgroundColor: 'rgba(255,255,255,0.04)', padding: '0.75rem', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginBottom: '0.25rem' }}>Direct Invitation Link:</div>
                  <div style={{ fontSize: '0.78rem', color: '#38bdf8', wordBreak: 'break-all', fontWeight: '700' }}>{meetingUrl}</div>
                </div>

                <button 
                  onClick={handleCopyLink}
                  style={{ width: '100%', backgroundColor: copiedLink ? '#16a34a' : '#0284c7', color: 'white', border: 'none', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', transition: 'background-color 0.2s' }}
                >
                  {copiedLink ? <><Check size={16} /> Invitation Link Copied!</> : <><Copy size={16} /> Copy Invitation Link</>}
                </button>
              </div>
            )}

          </div>
        )}

      </div>

      {/* BOTTOM ZOOM CONTROL BAR */}
      <div 
        style={{
          height: '75px',
          backgroundColor: '#0f172a',
          borderTop: '1px solid #1e293b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 1.5rem',
          flexShrink: 0,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.5)'
        }}
      >
        {/* LEFT CONTROLS: MIC & CAMERA */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          
          {/* Mute / Unmute Button */}
          <button 
            onClick={toggleAudio}
            title={isMuted ? "Unmute Microphone" : "Mute Microphone"}
            style={{
              backgroundColor: isMuted ? '#ef4444' : 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            {isMuted ? <MicOff size={18} /> : <Mic size={18} />}
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>{isMuted ? 'Unmute' : 'Mute'}</span>
          </button>

          {/* Start / Stop Video Button */}
          <button 
            onClick={toggleVideo}
            title={isVideoOff ? "Turn Video Camera On" : "Turn Video Camera Off"}
            style={{
              backgroundColor: isVideoOff ? '#ef4444' : 'rgba(255,255,255,0.1)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            {isVideoOff ? <VideoOff size={18} /> : <Video size={18} />}
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>{isVideoOff ? 'Start Video' : 'Stop Video'}</span>
          </button>
        </div>

        {/* CENTER CONTROLS: SECURITY, PARTICIPANTS, CHAT, SCREEN SHARE, RECORD, WHITEBOARD, REACTIONS */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          
          {/* Security */}
          <button 
            onClick={() => setActiveDrawer(activeDrawer === 'security' ? 'none' : 'security')}
            title="Meeting Security & Permissions"
            style={{
              backgroundColor: activeDrawer === 'security' ? '#0284c7' : 'rgba(255,255,255,0.06)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            <Shield size={18} />
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>Security</span>
          </button>

          {/* Participants */}
          <button 
            onClick={() => setActiveDrawer(activeDrawer === 'participants' ? 'none' : 'participants')}
            title="Participants List & Host Controls"
            style={{
              backgroundColor: activeDrawer === 'participants' ? '#0284c7' : 'rgba(255,255,255,0.06)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px',
              position: 'relative'
            }}
          >
            <Users size={18} />
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>Participants</span>
            <span style={{ position: 'absolute', top: '2px', right: '4px', backgroundColor: '#0284c7', color: 'white', fontSize: '0.6rem', padding: '0 0.3rem', borderRadius: '10px', fontWeight: '900' }}>
              {participants.length}
            </span>
          </button>

          {/* Chat */}
          <button 
            onClick={() => setActiveDrawer(activeDrawer === 'chat' ? 'none' : 'chat')}
            title="In-Meeting Judicial Chat & Citations"
            style={{
              backgroundColor: activeDrawer === 'chat' ? '#0284c7' : 'rgba(255,255,255,0.06)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            <MessageSquare size={18} />
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>Chat</span>
          </button>

          {/* Share Screen */}
          <button 
            onClick={toggleScreenShare}
            title={isScreenSharing ? "Stop Sharing Screen" : "Share Screen / Precedents / Drafts"}
            style={{
              backgroundColor: isScreenSharing ? '#22c55e' : 'rgba(255,255,255,0.06)',
              color: isScreenSharing ? '#0f172a' : '#22c55e',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            <ScreenShare size={18} />
            <span style={{ fontSize: '0.68rem', fontWeight: '800' }}>{isScreenSharing ? 'Sharing' : 'Share Screen'}</span>
          </button>

          {/* SUPERUSER / ADMIN RECORDING BUTTON */}
          <button 
            onClick={handleToggleRecording}
            title={canRecordMeeting ? (isRecording ? "Stop Audio/Video Recording" : "Record Session Audio & Video") : "Recording Restricted: Superuser / Database Admin permission required"}
            style={{
              backgroundColor: isRecording ? '#ef4444' : canRecordMeeting ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
              color: isRecording ? 'white' : canRecordMeeting ? '#ef4444' : '#64748b',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            <Disc size={18} className={isRecording ? 'animate-spin' : ''} />
            <span style={{ fontSize: '0.68rem', fontWeight: '800' }}>
              {isRecording ? 'Stop Rec' : canRecordMeeting ? 'Record' : '🔒 Record'}
            </span>
          </button>

          {/* Whiteboard */}
          <button 
            onClick={() => setActiveDrawer(activeDrawer === 'whiteboard' ? 'none' : 'whiteboard')}
            title="Interactive Whiteboard & Case Outline Canvas"
            style={{
              backgroundColor: activeDrawer === 'whiteboard' ? '#0284c7' : 'rgba(255,255,255,0.06)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.45rem 0.75rem',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.2rem',
              cursor: 'pointer',
              minWidth: '55px'
            }}
          >
            <PenTool size={18} />
            <span style={{ fontSize: '0.68rem', fontWeight: '700' }}>Whiteboard</span>
          </button>

          {/* Raise Hand & Reactions */}
          <div style={{ display: 'flex', gap: '0.25rem', backgroundColor: 'rgba(255,255,255,0.04)', padding: '0.2rem', borderRadius: '8px' }}>
            <button 
              onClick={toggleHandRaise}
              title={isHandRaised ? "Lower Hand" : "Raise Hand"}
              style={{
                backgroundColor: isHandRaised ? '#eab308' : 'transparent',
                color: isHandRaised ? '#0f172a' : 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '0.35rem 0.5rem',
                fontSize: '0.75rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.2rem'
              }}
            >
              ✋ {isHandRaised ? 'Lower' : 'Raise'}
            </button>
            <button onClick={() => sendReaction('👍')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>👍</button>
            <button onClick={() => sendReaction('👏')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>👏</button>
            <button onClick={() => sendReaction('⚖️')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}>⚖️</button>
          </div>

        </div>

        {/* RIGHT CONTROLS: END / LEAVE MEETING */}
        <div>
          <button 
            onClick={onClose}
            style={{
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '0.55rem 1.25rem',
              fontSize: '0.85rem',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.4)'
            }}
          >
            <Phone size={16} style={{ transform: 'rotate(135deg)' }} />
            {isHost ? 'End Meeting' : 'Leave Meeting'}
          </button>
        </div>

      </div>

      {/* RESTRICTED RECORDING MODAL (SUPERUSER/ADMIN RBAC) */}
      {showRecordingRestrictedModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 999999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ backgroundColor: '#0f172a', border: '2px solid #ef4444', borderRadius: '16px', maxWidth: '480px', width: '100%', padding: '1.75rem', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.8)' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
              <Lock size={28} />
            </div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: '900', color: '#f8fafc', margin: '0 0 0.5rem 0' }}>
              Judicial Recording Permission Required
            </h3>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8', lineHeight: '1.5', margin: '0 0 1.25rem 0' }}>
              Under Judicial Privacy and Legal Evidence Regulations, audio and video recording is strictly restricted. Only <strong>Superusers</strong> and <strong>Database Administrators</strong> are authorized to conduct session recordings or grant recording permissions.
            </p>
            <button 
              onClick={() => setShowRecordingRestrictedModal(false)}
              style={{ width: '100%', backgroundColor: '#0284c7', color: 'white', border: 'none', padding: '0.65rem', borderRadius: '8px', fontWeight: '800', fontSize: '0.9rem', cursor: 'pointer' }}
            >
              Understood
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default ElumVideoConferenceModal;
